import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  Trash2,
  Package,
  Check,
  X,
  Pencil,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const API_BASE = "https://nissi-india.com/api";

const BranchItems = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const navigate = useNavigate();

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [limit] = useState(10);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [editRowId, setEditRowId] = useState(null);
  const [editData, setEditData] = useState({});

  const [filters, setFilters] = useState({
    branchid: branchId || "",
    storeid: "",
    itemcategoryid: "",
    itemname: "",
    itemcode: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    branchid: branchId || "",
    storeid: "",
    itemcategoryid: "",
    itemname: "",
    itemcode: "",
  });

  const [isInitialReady, setIsInitialReady] = useState(false);
  const initialLoadDone = useRef(false);

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API_BASE}/branchItems_branches`, {
        headers: commonHeaders,
      });

      if (Array.isArray(res.data?.branches)) {
        const branchList = res.data.branches;
        setBranches(branchList);

        const defaultBranch =
          branchId || branchList?.[0]?.fld_Branchid?.toString() || "";

        setFilters((prev) => ({
          ...prev,
          branchid: prev.branchid || defaultBranch,
        }));

        return defaultBranch;
      } else {
        toast.error("Unexpected branches response.");
        return "";
      }
    } catch (err) {
      console.error("Branch Fetch Error:", err);
      toast.error("Failed to fetch branches.");
      return "";
    }
  };

  const fetchStores = async (selectedBranchId, preserveStore = true) => {
    if (!selectedBranchId) {
      setStores([]);
      return "";
    }

    try {
      const res = await axios.get(`${API_BASE}/branchItems_stores`, {
        params: { branchid: selectedBranchId },
        headers: commonHeaders,
      });

      if (Array.isArray(res.data?.stores)) {
        const storeList = res.data.stores;
        setStores(storeList);

        const defaultStore = storeList?.[0]?.fld_storeid?.toString() || "";

        setFilters((prev) => ({
          ...prev,
          storeid: preserveStore ? prev.storeid || defaultStore : defaultStore,
        }));

        return preserveStore ? filters.storeid || defaultStore : defaultStore;
      } else {
        toast.error("Unexpected stores response.");
        return "";
      }
    } catch (err) {
      console.error("Store Fetch Error:", err);
      toast.error("Failed to fetch stores.");
      return "";
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE}/branchItems_cat`, {
        headers: commonHeaders,
      });

      if (Array.isArray(res.data?.cat)) {
        setCategories(res.data.cat);
      } else {
        toast.error("Unexpected categories response.");
      }
    } catch (err) {
      console.error("Category Fetch Error:", err);
      toast.error("Failed to fetch categories.");
    }
  };

  const fetchItems = async (searchFilters = appliedFilters, searchPage = page) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/branchItems_list`, {
        params: {
          branchid: searchFilters.branchid || "",
          storeid: searchFilters.storeid || "",
          itemcategoryid: searchFilters.itemcategoryid || "",
          itemname: searchFilters.itemname || "",
          itemcode: searchFilters.itemcode || "",
          page: searchPage,
          pageSize: limit,
        },
        headers: commonHeaders,
      });

      const rows = res.data?.items || [];
      const backendTotalCount =
        Number(res.data?.totalCount || res.data?.count || 0) || 0;

      setItems(rows);
      setTotalCount(backendTotalCount);

      const calculatedPages =
        backendTotalCount > 0 ? Math.ceil(backendTotalCount / limit) : 1;

      setTotalPages(calculatedPages);
    } catch (err) {
      console.error("Failed to fetch items:", err);
      toast.error(
        err?.response?.data?.details ||
          err?.response?.data?.error ||
          "Failed to fetch items."
      );
      setItems([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const nextFilters = { ...filters };
    setSelectedItems([]);
    setEditRowId(null);
    setEditData({});
    setPage(1);
    setAppliedFilters(nextFilters);
  };

  const handleEditClick = (item) => {
    setEditRowId(item.fld_branchitemid);
    setEditData({
      fld_minstock: item.fld_minstock ?? 0,
      fld_location: item.fld_location ?? "",
      fld_itemPrice_New: item.fld_itemPrice_New ?? "",
      fld_itemHSNNo: item.fld_itemHSNNo ?? "",
    });
  };

  const handleCancelEdit = () => {
    setEditRowId(null);
    setEditData({});
  };

  const handleEditChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateItem = async (item) => {
    try {
      const payload = {
        branchitemid: item.fld_branchitemid,
        itemid: item.fld_itemid,
        branchid: Number(item.fld_branchid || filters.branchid || branchId || 0),
        minstock: Number(editData.fld_minstock ?? item.fld_minstock ?? 0),
        maxstock: Number(item.fld_maxstock ?? 0),
        newmaxstock: Number(item.fld_newmaxstock ?? 0),
        price:
          parseFloat(
            String(editData.fld_itemPrice_New ?? item.fld_itemPrice_New ?? "0").replace(
              /[^\d.]/g,
              ""
            )
          ) || 0,
        hsncode: editData.fld_itemHSNNo ?? item.fld_itemHSNNo ?? "",
        location: editData.fld_location ?? item.fld_location ?? "",
        serialData: Array.isArray(item.serialData) ? item.serialData : [],
        storeid: Number(filters.storeid || item.fld_storeid || 0),
        userid: Number(userId || 0),
      };

      const res = await axios.put(`${API_BASE}/branchItems_update`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        toast.success("Item updated successfully");
        setEditRowId(null);
        setEditData({});
        fetchItems(appliedFilters, page);
      } else {
        toast.error("Update failed");
      }
    } catch (err) {
      console.error("Update Error:", err);
      toast.error(
        err?.response?.data?.details ||
          err?.response?.data?.error ||
          "Error updating item"
      );
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(items.map((item) => item.fld_branchitemid));
    } else {
      setSelectedItems([]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedItems.length} item(s)?`
    );
    if (!confirmDelete) return;

    try {
      const res = await axios.post(
        `${API_BASE}/branchItems_delete`,
        { ids: selectedItems },
        { headers: commonHeaders }
      );

      if (res.data?.success) {
        toast.success("Items deleted successfully");
        setSelectedItems([]);
        fetchItems(appliedFilters, page);
      } else {
        toast.error("Failed to delete items");
      }
    } catch (err) {
      console.error("Delete Error:", err);
      toast.error(
        err?.response?.data?.details ||
          err?.response?.data?.error ||
          "Error deleting items"
      );
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      const defaultBranch = await fetchBranches();
      await fetchCategories();

      let defaultStore = "";
      if (defaultBranch) {
        defaultStore = await fetchStores(defaultBranch, false);
      }

      const initialFilters = {
        branchid: defaultBranch || branchId || "",
        storeid: defaultStore || "",
        itemcategoryid: "",
        itemname: "",
        itemcode: "",
      };

      setFilters(initialFilters);
      setAppliedFilters(initialFilters);
      setIsInitialReady(true);
    };

    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      loadInitialData();
    }
  }, []);

  useEffect(() => {
    if (!isInitialReady) return;
    fetchItems(appliedFilters, page);
  }, [appliedFilters, page, isInitialReady]);

  const handleBranchChange = async (value) => {
    setFilters((prev) => ({
      ...prev,
      branchid: value,
      storeid: "",
    }));

    const defaultStore = await fetchStores(value, false);

    setFilters((prev) => ({
      ...prev,
      branchid: value,
      storeid: defaultStore || "",
    }));
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Branch Items</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

<div className="space-y-6 mt-6 overflow-x-hidden">
          {/* Filters Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold mb-1">Branch Items</h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Manage and monitor branch inventory items
              </p>
            </div>

            <button
              onClick={() => navigate("/inventory/BranchItems-Add")}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${`bg-${baseColor}-${baseShade}`} hover:bg-${baseColor}-700`}
            >
              <Plus size={18} />
              Add New Item
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Branch */}
            <div className="space-y-1.5">
              <label className={`text-xs font-semibold tracking-wide ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Branch
              </label>
              <select
                className={`w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  isDark
                    ? "bg-gray-800/50 text-gray-200 border-gray-600"
                    : "bg-white text-black border-gray-300"
                }`}
                value={filters.branchid}
                onChange={(e) => handleBranchChange(e.target.value)}
              >
                <option value="">Select Branch</option>
                {branches.map((branchRow) => (
                  <option key={branchRow.fld_Branchid} value={branchRow.fld_Branchid}>
                    {branchRow.fld_BranchName}
                  </option>
                ))}
              </select>
            </div>

            {/* Store */}
            <div className="space-y-1.5">
              <label className={`text-xs font-semibold tracking-wide ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Store
              </label>
              <select
                className={`w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  isDark
                    ? "bg-gray-800/50 text-gray-200 border-gray-600"
                    : "bg-white text-black border-gray-300"
                }`}
                value={filters.storeid}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, storeid: e.target.value }))
                }
              >
                <option value="">Select Store</option>
                {stores.map((store) => (
                  <option key={store.fld_storeid} value={store.fld_storeid}>
                    {store.fld_storename || store.Fld_StoreName}
                  </option>
                ))}
              </select>
            </div>

            {/* Category */}
            <div className="space-y-1.5">
              <label className={`text-xs font-semibold tracking-wide ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Category
              </label>
              <select
                className={`w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  isDark
                    ? "bg-gray-800/50 text-gray-200 border-gray-600"
                    : "bg-white text-black border-gray-300"
                }`}
                value={filters.itemcategoryid}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    itemcategoryid: e.target.value,
                  }))
                }
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.fld_itemcategoryid} value={cat.fld_itemcategoryid}>
                    {cat.fld_itemcategory_name || "(Unnamed Category)"}
                  </option>
                ))}
              </select>
            </div>

            {/* Item Name */}
            <div className="space-y-1.5">
              <label className={`text-xs font-semibold tracking-wide ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Item Name
              </label>
              <input
                type="text"
                placeholder="Search by name..."
                className={`w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  isDark
                    ? "bg-gray-800/50 text-gray-200 border-gray-600 placeholder:text-gray-400"
                    : "bg-white text-black border-gray-300 placeholder:text-gray-500"
                }`}
                value={filters.itemname}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, itemname: e.target.value }))
                }
              />
            </div>

            {/* Item Code */}
            <div className="space-y-1.5">
              <label className={`text-xs font-semibold tracking-wide ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Item Code
              </label>
              <input
                type="text"
                placeholder="Search by code..."
                className={`w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                  isDark
                    ? "bg-gray-800/50 text-gray-200 border-gray-600 placeholder:text-gray-400"
                    : "bg-white text-black border-gray-300 placeholder:text-gray-500"
                }`}
                value={filters.itemcode}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, itemcode: e.target.value }))
                }
              />
            </div>

            {/* Search Button */}
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-lg shadow transition ${`bg-${baseColor}-${baseShade}`} hover:bg-${baseColor}-700`}
              >
                <Search size={16} />
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-xl font-bold">Items List</h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {totalCount} items
                </span>
              </div>

              <button
                onClick={handleDeleteSelected}
                disabled={selectedItems.length === 0}
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition ${
                  selectedItems.length > 0
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                <Trash2 size={16} />
                Delete ({selectedItems.length})
              </button>
            </div>
          </div>

<div className="overflow-x-auto max-w-full">
            <table className="w-full min-w-[1400px] text-sm">
              <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={items.length > 0 && selectedItems.length === items.length}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Code</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Name</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Part No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">UOM</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">OP Stock</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap min-w-[110px]">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap min-w-[100px]">Item Price</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Total Price</th>
<th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap min-w-[90px]">Min Stock</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">OP Date</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Serial No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Branch</th>
<th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap min-w-[90px]">HSN Code</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Actions</th>
                </tr>
              </thead>

              <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(16)].map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton height={20} baseColor={isDark ? "#374151" : "#f3f4f6"} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={16} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <Package size={48} className="text-gray-400" />
                        <div className="text-lg font-semibold text-gray-500">No items found</div>
                        <div className="text-sm text-gray-400">
                          Try adjusting your filters or add some items
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  items.map((item, i) => {
                    const isEditing = item.fld_branchitemid === editRowId;
                    const isSelected = selectedItems.includes(item.fld_branchitemid);

                    return (
                      <tr
                        key={item.fld_branchitemid}
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        } ${isSelected ? `bg-${baseColor}-50 dark:bg-${baseColor}-900/20` : ""} ${
                          isEditing ? "ring-2 ring-blue-400" : ""
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItems((prev) => [...prev, item.fld_branchitemid]);
                              } else {
                                setSelectedItems((prev) =>
                                  prev.filter((id) => id !== item.fld_branchitemid)
                                );
                              }
                            }}
                            className="w-4 h-4 rounded"
                          />
                        </td>

                        <td className="px-4 py-3">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {(page - 1) * pageSize + i + 1}
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {item.fld_itemcode}
                          </span>
                        </td>

                        <td className="px-4 py-3 font-medium">{item.fld_itemname}</td>
                        <td className="px-4 py-3">{item.fld_itemPartNo || "-"}</td>
                        <td className="px-4 py-3">{item.fld_uomname}</td>

                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                              Number(item.fld_opening_stock) > 0
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {item.fld_opening_stock}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          {isEditing ? (
                           <input
                              type="text"
                              value={editData.fld_location}
                              onChange={(e) => handleEditChange("fld_location", e.target.value)}
                              className="w-full min-w-[110px] px-2 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                              placeholder="Location"
                            />
                          ) : (
                            item.fld_location || "-"
                          )}
                        </td>

                        <td className="px-4 py-3">
                          {isEditing ? (
                            <input
                              type="number"
                              value={editData.fld_itemPrice_New}
                              onChange={(e) =>
                                handleEditChange("fld_itemPrice_New", e.target.value)
                              }
                              className="w-full min-w-[100px] px-2 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                              placeholder="Price"
                              step="0.01"
                            />
                          ) : (
                            <span className="font-semibold text-green-600">
      {/* ✅ FIX: fld_itemPrice_New is already a formatted currency
          STRING from the SP (e.g. "₹1,234.00") — parseFloat() on that
          returns NaN. Use the raw numeric fld_itemPrice instead. */}
      ₹{parseFloat(item.fld_itemPrice || 0).toLocaleString()}
    </span>
                          )}
                        </td>

                        <td className="px-4 py-3">
  <span className="font-semibold text-blue-600">
    {/* ✅ FIX: same issue — TotValue_New is pre-formatted, use raw TotValue */}
    ₹{parseFloat(item.TotValue || 0).toLocaleString()}
  </span>
</td>

                        <td className="px-4 py-3">
                          {isEditing ? (
     <input
                              type="number"
                              value={editData.fld_minstock}
                              onChange={(e) => handleEditChange("fld_minstock", e.target.value)}
                              className="w-full min-w-[90px] px-2 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                              placeholder="Min Stock"
                            />
                          ) : (
                            <span
                              className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                                Number(item.fld_minstock) > 0
                                  ? "bg-orange-100 text-orange-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {item.fld_minstock || 0}
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-gray-500">
                          {item.fld_date
                            ? new Date(item.fld_date).toLocaleDateString()
                            : "-"}
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                              item.serialno
                                ? "bg-purple-100 text-purple-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {item.serialno || "N/A"}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-indigo-600 dark:text-indigo-400">
                          {item.fld_BranchName}
                        </td>

                        <td className="px-4 py-3">
                          {isEditing ? (
<input
                              type="text"
                              value={editData.fld_itemHSNNo}
                              onChange={(e) => handleEditChange("fld_itemHSNNo", e.target.value)}
                              className="w-full min-w-[90px] px-2 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                              placeholder="HSN Code"
                            />
                          ) : (
                            <span className="font-mono text-sm">
                              {item.fld_itemHSNNo || "-"}
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-3">
                          {isEditing ? (
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleUpdateItem(item)}
                                className="p-1.5 rounded-full bg-green-100 text-green-600 hover:bg-green-200 transition"
                                title="Save"
                              >
                                <Check size={16} />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="p-1.5 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition"
                                title="Cancel"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex justify-center">
                              <button
                                onClick={() => handleEditClick(item)}
                                className="p-1.5 rounded-full bg-yellow-100 text-yellow-600 hover:bg-yellow-200 transition"
                                title="Edit"
                              >
                                <Pencil size={16} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination + Stats */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Showing{" "}
              <span className="font-bold">
                {items.length ? (page - 1) * limit + 1 : 0}
              </span>{" "}
              to{" "}
              <span className="font-bold">{Math.min(page * limit, totalCount)}</span> of{" "}
              <span className="font-bold">{totalCount}</span> results
            </div>

            {loading ? (
              <div className="flex space-x-2">
                <Skeleton width={80} height={36} borderRadius={10} />
                <Skeleton width={36} height={36} borderRadius={10} />
                <Skeleton width={36} height={36} borderRadius={10} />
                <Skeleton width={80} height={36} borderRadius={10} />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  <ChevronLeft size={16} />
                  Prev
                </button>

                <div className="flex items-center gap-1">
                  {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
                    const pageNumber = idx + 1;
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setPage(pageNumber)}
                        className={`w-9 h-9 text-sm font-medium rounded-lg transition ${
                          page === pageNumber
                            ? `bg-${baseColor}-${baseShade} text-white`
                            : "hover:bg-gray-100 dark:hover:bg-gray-800"
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}

                  {totalPages > 5 && (
                    <>
                      <span className="px-1 text-gray-400">...</span>
                      <button
                        onClick={() => setPage(totalPages)}
                        className={`w-9 h-9 text-sm font-medium rounded-lg transition ${
                          page === totalPages
                            ? `bg-${baseColor}-${baseShade} text-white`
                            : "hover:bg-gray-100 dark:hover:bg-gray-800"
                        }`}
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>

                <button
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page === totalPages}
                  className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div className={`p-3 rounded-xl ${isDark ? "bg-gray-800/30" : "bg-gray-50"}`}>
                <div className="text-2xl font-bold text-blue-600">{totalCount}</div>
                <div className="text-xs text-gray-500 mt-1">Total Items</div>
              </div>
              <div className={`p-3 rounded-xl ${isDark ? "bg-gray-800/30" : "bg-gray-50"}`}>
                <div className="text-2xl font-bold text-green-600">{selectedItems.length}</div>
                <div className="text-xs text-gray-500 mt-1">Selected</div>
              </div>
              <div className={`p-3 rounded-xl ${isDark ? "bg-gray-800/30" : "bg-gray-50"}`}>
                <div className="text-2xl font-bold text-purple-600">
                  {editRowId ? 1 : 0}
                </div>
                <div className="text-xs text-gray-500 mt-1">Editing</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default BranchItems;